import React, { useState } from 'react';
import Container from '../container';
import styled, { css } from 'styled-components';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import useEmblaCarousel from 'embla-carousel-react';
import Chevron from '../../images/svg/chevron-icon.svg';
import ContentCarouselSliderItem from './ContentCarouselSliderItem';
import mq from '../../utils/mq';
import Button from '../button';
import parseStoryblokLink from '../../utils/parseStoryblokLink';

const OuterContainer = styled.div`
  width: 100%;
  height: 100%;
  background: var(--Background-bg-inverse, #111);
  padding: 96px 0;
  ${mq.mobile(css`
    padding: 48px 0;
  `)};
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  letter-spacing: 0px;
  text-align: center;
  padding-bottom: 48px;
  &.light-theme {
    color: #161616;
  }
  ${mq.mobile(css`
    text-align: left;
    font-size: 36px;
  `)};
`;

const SliderNavigationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

const NavigationControlButton = styled.button<{ $disabled?: boolean }>`
  width: 48px;
  height: 48px;
  background: #645757;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  border: none;
  transition: all 0.15s ease-out;
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    border: 1px solid rgba(255, 255, 255, 1);
    backdrop-filter: blur(10px);
    color: #000;
    transition: all 0.15s ease-out;
  }

  &:active {
    transform: scale(0.97);
  }

  &.light-theme {
    background: #f8f8f8;
    color: #161616;
    &:hover {
      background-color: rgba(0, 0, 0, 1);
      border: 1px solid rgba(0, 0, 0, 1);
      color: #f8f8f8;
      transition: all 0.15s ease-out;
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      transition: all 0.3s ease-in-out;
    `}
  svg {
    width: 12px;
  }
`;

const ContentSliderOuter = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: visible;
  overscroll-behavior-x: contain;
`;

const ContentSliderContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: visible;
  overscroll-behavior-x: contain;
`;

const EmblaContainer = styled.div`
  touch-action: pan-y pinch-zoom;
  display: flex;
`;

const NavigationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding-top: 40px;
`;

const NavigationWithCtaContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding-top: 40px;
  ${mq.mobile(css`
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
  `)};
`;

interface ContentSlide {
  asset: any;
  title: string;
  bodyContent: any;
}

export default function ContentCarousel({
  title,
  contentSlides,
  ctaText,
  ctaLink
}: {
  title: string;
  contentSlides: ContentSlide[];
  ctaText?: string;
  ctaLink?: any;
}) {
  const [canScrollNext, setcanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      skipSnaps: true,
      dragThreshold: 0.2,
      //align: 'start',
      loop: true
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: 'x'
      })
    ]
  );

  const previousSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollPrev()) {
        emblaApi.scrollPrev();
      }
    }
  };

  const nextSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      }
    }
  };

  emblaApi?.on('scroll', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });

  emblaApi?.on('init', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });
  return (
    <OuterContainer>
      <ContentContainer>
        <Container>
          <Title>{title}</Title>
        </Container>
        <ContentSliderOuter className="embla">
          <ContentSliderContainer
            ref={emblaRef as any}
            className="embla__viewport"
          >
            <EmblaContainer className="embla__container">
              {/* Duplicate the content slides to ensure there are always enough slides to create the loop */}
              {[
                ...contentSlides,
                ...contentSlides,
                ...contentSlides,
                ...contentSlides,
                ...contentSlides
              ].map((contentSlide, index) => (
                <ContentCarouselSliderItem key={index} {...contentSlide} />
              ))}
            </EmblaContainer>
          </ContentSliderContainer>
        </ContentSliderOuter>
        <Container>
          {ctaText && ctaLink ? (
            <NavigationWithCtaContainer>
              <SliderNavigation
                previousSlide={previousSlide}
                canScrollPrev={canScrollPrev}
                nextSlide={nextSlide}
                canScrollNext={canScrollNext}
              />
              <Button to={parseStoryblokLink(ctaLink)} borderPurpleColorWhite>
                {ctaText}
              </Button>
            </NavigationWithCtaContainer>
          ) : (
            <NavigationContainer>
              <SliderNavigation
                previousSlide={previousSlide}
                canScrollPrev={canScrollPrev}
                nextSlide={nextSlide}
                canScrollNext={canScrollNext}
              />
            </NavigationContainer>
          )}
        </Container>
      </ContentContainer>
    </OuterContainer>
  );
}

const SliderNavigation = ({
  previousSlide,
  canScrollPrev,
  nextSlide,
  canScrollNext
}: {
  previousSlide: any;
  canScrollPrev: boolean;
  lightTheme?: boolean;
  nextSlide: any;
  canScrollNext: boolean;
}) => {
  return (
    <SliderNavigationControls>
      <NavigationControlButton
        onClick={previousSlide}
        $disabled={!canScrollPrev}
        style={{ paddingRight: '2px' }}
      >
        <Chevron />
      </NavigationControlButton>
      <NavigationControlButton
        onClick={nextSlide}
        $disabled={!canScrollNext}
        style={{ paddingLeft: '2px' }}
      >
        <Chevron style={{ transform: 'rotate(180deg)' }} />
      </NavigationControlButton>
    </SliderNavigationControls>
  );
};
